import React, { useEffect } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBriefcase,
  faCalendarDays,
  faListCheck,
  faFireFlameCurved,
  faGaugeHigh,
  faHouse,
  faRightFromBracket,
  faSquareCheck,
  faUsers,
  faBolt,
  faTrophy,
  faChartLine,
  faPeopleGroup,
  faCalendarDay,
  faCalendarWeek,
  faNoteSticky,
  faBullseye,
  faInbox,
  faLock,
  faBook,
  faCrosshairs,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./Sidebar.css";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { usePlan } from "../context/PlanContext";

// Menu declarado em dados para o plano poder decidir o que abre e o que
// aparece com cadeado. `recurso` ausente = disponivel em todos os planos.
const MENU_PRINCIPAL = [
  { rota: "/dashboard", label: "Dashboard", icone: faHouse },
  { rota: "/dashboard-gestor", label: "Dashboard Gestor", icone: faGaugeHigh, recurso: "dashboard_gestor" },
  { rota: "/onepage", label: "OnePage", icone: faBullseye, recurso: "onepage" },
  { rota: "/daily-gestor", label: "Daily Gestor", icone: faUsers, recurso: "daily_gestor" },
  { rota: "/tarefas", label: "Tarefas", icone: faListCheck },
  { rota: "/lembretes", label: "Lembretes", icone: faInbox },
  { rota: "/meu-dia", label: "Meu Dia", icone: faSquareCheck },
  { rota: "/habitos", label: "Habitos", icone: faFireFlameCurved, recurso: "habitos" },
  { rota: "/notes", label: "Notas", icone: faNoteSticky },
  { rota: "/planejamento-semana", label: "Planejamento", icone: faCalendarDays, recurso: "planejamento_semanal" },
  { rota: "/projetos", label: "Projetos", icone: faBriefcase },
  { rota: "/diario-desenvolvimento", label: "Diário de Desenvolvimento", icone: faBook },
];

const MENU_LEVELUP = [
  { rota: "/levelup", label: "Hub", icone: faBolt, recurso: "levelup_hub" },
  { rota: "/metas", label: "Metas", icone: faTrophy, recurso: "metas" },
  { rota: "/skills", label: "Skills", icone: faChartLine, recurso: "skills" },
  { rota: "/levelup/dia", label: "Planejar Dia", icone: faCalendarDay, recurso: "levelup_dia" },
  { rota: "/levelup/semana", label: "Visão Semanal", icone: faCalendarWeek, recurso: "levelup_semana" },
  { rota: "/team-pdi", label: "Time PDI", icone: faPeopleGroup, recurso: "team_pdi", classeExtra: "sidebar-item-manager" },
  { rota: "/okrs", label: "OKRs", icone: faCrosshairs, recurso: "okrs" },
];

const Sidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { temRecurso } = usePlan();

  function handleNavigate(path) {
    onClose();    
    navigate(path);
  }

  function handleLogout() {
    onClose();
    logout();
    navigate("/login");
  }

  useEffect(() => {
    if (open) {
      onClose();
    }
  }, [location.pathname]);

  // Item bloqueado continua visivel: o cadeado leva para a tela de planos,
  // que e onde a pessoa entende o que esta perdendo.
  function renderItem(item, classeBase) {
    const liberado = !item.recurso || temRecurso(item.recurso);
    const classes = [
      "sidebar-item",
      classeBase,
      item.classeExtra,
      liberado ? "" : "sidebar-item-bloqueado",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        key={item.rota}
        className={classes}
        title={liberado ? undefined : "Disponivel em um plano superior"}
        onClick={() =>
          handleNavigate(liberado ? item.rota : `/planos?recurso=${item.recurso}`)
        }
      >
        <FontAwesomeIcon icon={item.icone} className="sidebar-icon" /> {item.label}
        {!liberado && <FontAwesomeIcon icon={faLock} className="sidebar-lock" />}
      </div>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="sidebar-overlay"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="sidebar"
            initial={{ x: -260 }}
            animate={{ x: 0 }}
            exit={{ x: -260 }}
            transition={{ duration: 0.25 }}
          >
            <div className="sidebar-header" onClick={() => handleNavigate("/dashboard")}>
              LevelUp <span>ToDo</span>
            </div>

            {MENU_PRINCIPAL.map((item) => renderItem(item))}

            <div className="sidebar-section-label">LevelUp PDI</div>

            {MENU_LEVELUP.map((item) => renderItem(item, "sidebar-item-levelup"))}

            <div className="sidebar-footer">
              <div className="sidebar-item" onClick={() => handleNavigate("/planos")}>
                <FontAwesomeIcon icon={faBolt} className="sidebar-icon" /> Meu plano
              </div>

              <div className="sidebar-item sidebar-item-logout" onClick={handleLogout}>
                <FontAwesomeIcon icon={faRightFromBracket} className="sidebar-icon" /> Sair
              </div>
            </div>

          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
